import { withZod } from '@remix-validated-form/with-zod'
import { z } from 'zod'
import { Option } from 'app/components/selector/SelectAll'
import type { Permission } from 'app/types/entities'
import { isSMCOrg } from 'lib/isSMCOrg'
import type { ListCopy } from 'lib/listCopy'
import { OptionType } from 'types/OptionType'

export const roleValidator = withZod(
  z.object({
    name: z.string().min(1, { message: `Role name is required` }),
    permissions: z.union([z.array(z.string()), z.string()]).optional(),
  }),
)

export type PermissionGroup = {
  name: string
  value: Permission[]
  checked: boolean
  showIndividualOptions: boolean
  groupOptions: Array<{ name: string; value: Permission; checked: boolean }>
}

const workspacePagePermissionsData: Array<{ name: string; value: Permission; id: string }> = [
  { name: `Contacts`, value: `pages:contacts`, id: `pages:contacts` },
  { name: `Pipelines`, value: `pages:pipelines`, id: `pages:pipelines` },
  { name: `Tasks`, value: `pages:tasks`, id: `pages:tasks` },
  { name: `Inbox`, value: `pages:inbox`, id: `pages:inbox` },
  { name: `Analytics`, value: `pages:analytics`, id: `pages:analytics` },
  { name: `Activities`, value: `pages:activities`, id: `pages:activities` },
  { name: `Settings`, value: `pages:settings`, id: `pages:settings` },
]
export const WORKSPACE_PAGE_PERMISSIONS = workspacePagePermissionsData as typeof workspacePagePermissionsData &
  OptionType[]

const orgPagePermissionsData: Array<{ name: string; value: Permission; id: string }> = [
  { name: `Attribution`, value: `org:pages:analytics:attribution`, id: `org:pages:analytics:attribution` },
  { name: `Production`, value: `org:pages:analytics:revenue`, id: `org:pages:analytics:revenue` },
]
export const ORG_PAGE_PERMISSIONS = orgPagePermissionsData as typeof orgPagePermissionsData & OptionType[]

// The page permission tables above never rename: their `name` is what a role editor renders
// for a permission that still has no list wording of its own. Only the pipelines page permission
// picks up the list copy, so the forms read this instead of the raw table entry.
export function permissionDisplayName(permission: { name: string; value: string }, copy: ListCopy): string {
  if (permission.value === `pages:pipelines`) return copy.manualListsPermission
  return permission.name
}

export const ORG_DEFAULT_PAGE_PERMISSIONS: Permission[] = [
  `org:pages:companies`,
  `org:pages:contacts`,
  `org:pages:activities`,
  `org:pages:pipelines`,
  `org:pages:tasks`,
  `org:pages:inbox`,
  `org:pages:settings`,
  `org:pages:analytics:marketing`,
  `org:pages:analytics:new-patients`,
]

export const getWorkspacePermissionOptions = (orgId?: string): PermissionGroup[] => {
  const isSMC = isSMCOrg(orgId)

  const groups: PermissionGroup[] = []

  groups.push({
    name: `See Marketing Spend`,
    value: [`marketing:spend`],
    checked: false,
    showIndividualOptions: false,
    groupOptions: [],
  })

  const contactOptions = isSMC
    ? [
        { name: `Edit Channel`, value: `contacts:attributions` as Permission, checked: false },
        { name: `Merge Contacts`, value: `contacts:merge` as Permission, checked: false },
        { name: `Mark as Non-Patient`, value: `contacts:mark-non-patient` as Permission, checked: false },
        { name: `Edit Lead Status`, value: `contacts:lead-status` as Permission, checked: false },
      ]
    : [
        { name: `Edit Qualification`, value: `contacts:qualifications` as Permission, checked: false },
        { name: `Edit Channel`, value: `contacts:attributions` as Permission, checked: false },
        { name: `Merge Contacts`, value: `contacts:merge` as Permission, checked: false },
        { name: `Mark as Non-Patient`, value: `contacts:mark-non-patient` as Permission, checked: false },
        { name: `Edit Lead Status`, value: `contacts:lead-status` as Permission, checked: false },
      ]

  groups.push({
    name: `Contacts Permissions`,
    value: contactOptions.map((o) => o.value),
    checked: false,
    showIndividualOptions: true,
    groupOptions: contactOptions,
  })

  groups.push({
    name: `Staff Performance Metrics`,
    value: [`staff:performance:own`, `staff:performance:others`],
    checked: false,
    showIndividualOptions: true,
    groupOptions: [
      { name: `See Your Performance`, value: `staff:performance:own`, checked: false },
      { name: `See Other User's Performance`, value: `staff:performance:others`, checked: false },
    ],
  })

  const settingsOptions = isSMC
    ? [
        { name: `Manage Users`, value: `users:manage` as Permission, checked: false },
        { name: `Manage Staff`, value: `staff:manage` as Permission, checked: false },
        { name: `Manage Roles`, value: `roles:manage` as Permission, checked: false },
        { name: `Manage Referral Mappings`, value: `settings:mapping:edit` as Permission, checked: false },
        { name: `Manage Workspace`, value: `settings:workspace` as Permission, checked: false },
        { name: `Manage Location`, value: `settings:location` as Permission, checked: false },
        { name: `View Tags`, value: `settings:tags:view` as Permission, checked: false },
        { name: `Manage Tags`, value: `settings:tags:edit` as Permission, checked: false },
        { name: `Manage Scheduling`, value: `settings:scheduling` as Permission, checked: false },
        { name: `Delete Shared Lists`, value: `lists:delete-shared` as Permission, checked: false },
      ]
    : [
        { name: `Manage Users`, value: `users:manage` as Permission, checked: false },
        { name: `Manage Staff`, value: `staff:manage` as Permission, checked: false },
        { name: `Manage Roles`, value: `roles:manage` as Permission, checked: false },
        { name: `See Integrations`, value: `settings:integrations` as Permission, checked: false },
        { name: `View Mappings`, value: `settings:mapping:view` as Permission, checked: false },
        { name: `Manage Mappings`, value: `settings:mapping:edit` as Permission, checked: false },
        { name: `Manage Workspace`, value: `settings:workspace` as Permission, checked: false },
        { name: `Manage Location`, value: `settings:location` as Permission, checked: false },
        { name: `View Tags`, value: `settings:tags:view` as Permission, checked: false },
        { name: `Manage Tags`, value: `settings:tags:edit` as Permission, checked: false },
        { name: `Manage Budget`, value: `settings:budget` as Permission, checked: false },
        { name: `Manage Scheduling`, value: `settings:scheduling` as Permission, checked: false },
        { name: `Delete Shared Lists`, value: `lists:delete-shared` as Permission, checked: false },
      ]

  groups.push({
    name: `Settings`,
    value: settingsOptions.map((o) => o.value),
    checked: false,
    showIndividualOptions: true,
    groupOptions: settingsOptions,
  })

  return groups
}

export const getOrgOrganizationPermissions = (): PermissionGroup[] => [
  {
    name: `Create Workspace & Location`,
    value: [`org:create:workspace`, `org:create:location`],
    checked: false,
    showIndividualOptions: false,
    groupOptions: [],
  },
  {
    name: `See Organization Integrations`,
    value: [`org:settings:see-integrations`],
    checked: false,
    showIndividualOptions: false,
    groupOptions: [],
  },
  {
    name: `Manage Users`,
    value: [`org:settings:manage-users`],
    checked: false,
    showIndividualOptions: false,
    groupOptions: [],
  },
  {
    name: `Manage Roles`,
    value: [`org:settings:manage-roles`],
    checked: false,
    showIndividualOptions: false,
    groupOptions: [],
  },
]

export const getOrgGlobalPermissions = (): PermissionGroup[] => {
  const contactsGroup: PermissionGroup = {
    name: `Contacts Permissions`,
    value: [
      `org:contacts:see-utm`,
      `org:contacts:edit-channel`,
      `org:contacts:edit-qualification`,
      `org:contacts:edit-lead-status`,
      `org:contacts:set-non-patient`,
    ],
    checked: false,
    showIndividualOptions: true,
    groupOptions: [
      { name: `See Attribution Fields`, value: `org:contacts:see-utm`, checked: false },
      { name: `Edit Channel`, value: `org:contacts:edit-channel`, checked: false },
      { name: `Edit Qualification`, value: `org:contacts:edit-qualification`, checked: false },
      { name: `Edit Lead Status`, value: `org:contacts:edit-lead-status`, checked: false },
      { name: `Set Non Patient`, value: `org:contacts:set-non-patient`, checked: false },
    ],
  }

  const groups: PermissionGroup[] = [contactsGroup]

  groups.push({
    name: `See Marketing Spend`,
    value: [`org:marketing:see-spend`],
    checked: false,
    showIndividualOptions: false,
    groupOptions: [],
  })

  groups.push({
    name: `See Advanced Marketing Filters`,
    value: [`org:marketing:advanced-filters`],
    checked: false,
    showIndividualOptions: false,
    groupOptions: [],
  })

  return groups
}

type PermissionSection = { label: string; groups: PermissionGroup[] }

type RoleScope = {
  pagePermissions: typeof WORKSPACE_PAGE_PERMISSIONS
  fixedPagePermissions: Permission[]
  permissionSections: (orgId?: string) => PermissionSection[]
}

export const ORG_ROLE_SCOPE: Readonly<RoleScope> = {
  pagePermissions: ORG_PAGE_PERMISSIONS,
  fixedPagePermissions: ORG_DEFAULT_PAGE_PERMISSIONS,
  permissionSections: () => [
    { label: `Organization Permissions`, groups: getOrgOrganizationPermissions() },
    { label: `Global Permissions`, groups: getOrgGlobalPermissions() },
  ],
}

export const WORKSPACE_ROLE_SCOPE: Readonly<RoleScope> = {
  pagePermissions: WORKSPACE_PAGE_PERMISSIONS,
  fixedPagePermissions: [],
  permissionSections: (orgId) => [{ label: `Permissions`, groups: getWorkspacePermissionOptions(orgId) }],
}

export const initializePermissionState = (options: PermissionGroup[], rolePermissions?: Permission[]): Option[] => {
  if (!rolePermissions) return options as Option[]
  return options.map((group) => {
    if (group.showIndividualOptions && group.groupOptions.length > 0) {
      const groupOptions = group.groupOptions.map((opt) => ({
        ...opt,
        checked: rolePermissions.includes(opt.value as Permission) || false,
      }))
      const allChildrenChecked = groupOptions.every((opt) => opt.checked)
      return { ...group, checked: allChildrenChecked, groupOptions }
    }
    const isChecked = group.value.every((v) => rolePermissions.includes(v as Permission))
    return { ...group, checked: isChecked, groupOptions: [] }
  }) as Option[]
}

export const extractPermissions = (options: Option[]): string[] =>
  options.flatMap((group) => {
    if (group.showIndividualOptions && group.groupOptions?.length) {
      return group.groupOptions.filter((opt) => opt.checked).map((opt) => opt.value)
    }
    return group.checked ? group.value : []
  })

export const filterPermissionGroups = (
  groups: PermissionGroup[],
  userPermissions: string[] | undefined,
  hasAllPermissions: boolean,
): PermissionGroup[] => {
  if (hasAllPermissions) return groups
  return groups
    .map((group) => {
      const filteredGroupOptions = group.groupOptions?.filter((opt) => userPermissions?.includes(opt.value)) || []
      if (group.showIndividualOptions && filteredGroupOptions.length === 0) return null
      if (!group.showIndividualOptions && !group.value.some((v) => userPermissions?.includes(v))) return null
      return {
        ...group,
        value: filteredGroupOptions.length > 0 ? filteredGroupOptions.map((opt) => opt.value) : group.value,
        groupOptions: filteredGroupOptions,
      }
    })
    .filter(Boolean) as PermissionGroup[]
}

export const getPagePermissionValues = (
  pagePermissions: string[],
  availablePagePermissions: OptionType[],
): OptionType[] => {
  const selectedFromAvailable = availablePagePermissions.filter((opt) => pagePermissions.includes(opt.value))

  if (selectedFromAvailable.length === availablePagePermissions.length && availablePagePermissions.length > 0) {
    return [{ name: `All Pages`, id: `0`, value: `` }] as OptionType[]
  }

  return selectedFromAvailable
}
